/**
 * Quick notes.
 *
 * `QuickNoteCapture` sits in the application shell, so a note starts from any
 * page in a few seconds. `NotesOnRecord` shows the filed notes beside one
 * record, and it states that a note is not evidence.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { RecoveryBanner, clearRecovery, useRecoveredText } from "./recovery.tsx";

import { api } from "../lib/api.ts";
import { dateOnly } from "../lib/dates.ts";
import { useProblem } from "../lib/problem.ts";
import { Button, IconButton, Note, Problem, classes, inputClass } from "./primitives.tsx";
import { useDismiss } from "./use-dismiss.ts";

export interface NoteLink {
  readonly recordType: string;
  readonly recordId: string;
}

export interface NoteRow {
  readonly id: string;
  readonly body: string;
  readonly state: "unfiled" | "filed" | "dropped";
  readonly authorName: string;
  readonly ageDays: number;
  readonly filedAt: string | null;
  readonly createdAt: string;
  readonly links: readonly NoteLink[];
}

/**
 * The box that writes one note.
 *
 * On a wide screen it is a small popover under the header button. On a phone
 * it is a sheet that fills the screen, opened from a fixed button at the
 * bottom, because writing a note is the most common thing a person does
 * there. Either closes on Escape, on a tap outside, or on its own Close.
 */
export function QuickNoteCapture({
  inline = false,
  onOpen,
}: {
  /** Draw a plain button that is always visible, for the phone's More sheet. */
  readonly inline?: boolean;
  /** Called when the panel opens, so a sheet that holds the trigger can close. */
  readonly onOpen?: () => void;
} = {}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const { problem, setProblem, onError } = useProblem();
  const [saved, setSaved] = useState(false);
  const recovery = useRecoveredText("quick-note", body, setBody);
  const panel = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useDismiss(panel, open, close);

  useEffect(() => {
    if (open) {
      // The keyboard opens with the sheet, so the person types at once.
      field.current?.focus();
    }
  }, [open]);

  const write = useMutation({
    mutationFn: () => api.post<NoteRow>("/api/notes", { body }),
    onSuccess: async () => {
      setBody("");
      setProblem(null);
      setSaved(true);
      clearRecovery("quick-note");
      field.current?.focus();
      await queryClient.invalidateQueries({ queryKey: ["notes-unfiled"] });
    },
    onError,
  });

  const toggle = () => {
    setOpen(!open);
    setSaved(false);
  };

  return (
    <>
      {/*
        The trigger. On a wide screen it is a control in the header. On a
        phone it is a button in the More sheet, which is where the phone keeps
        the two things a person writes from anywhere — there is no floating
        button any more, because a round `+` that could mean six different
        things means none of them, and the bottom navigation is now where a
        thumb goes.
      */}
      <span className={inline ? "flex flex-1 [&>button]:flex-1" : "hidden sm:inline-block"}>
        <Button
          size="small"
          ariaExpanded={open}
          onClick={() => {
            if (!open) onOpen?.();
            toggle();
          }}
        >
          Quick note
        </Button>
      </span>

      {open && (
        <>
          {/* The backdrop on a phone. A tap on it closes the sheet. */}
          <div
            className={classes(
              "fixed inset-0 bg-ink/30 sm:hidden",
              // Above the More sheet when the button that opened this is in it.
              inline ? "z-[55]" : "z-30",
            )}
            aria-hidden="true"
          />
          <div
            ref={panel}
            role="dialog"
            aria-label="Quick note"
            className={classes(inline ? "z-[60]" : "z-40", `fixed inset-x-0 bottom-0 flex flex-col gap-3 rounded-t-arc border-t border-divider bg-paper p-4 shadow-xl
                       sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-1 sm:w-96 sm:rounded-arc sm:border sm:p-3`)}
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
          >
            <form
              className="flex flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                write.mutate();
              }}
            >
              <div className="flex items-center justify-between">
                <label htmlFor="quick-note-body" className="font-medium">
                  Quick note
                </label>
                <IconButton icon="close" label="Close" onClick={close} />
              </div>
              <p className="text-sm text-muted">Write the thought. File it later from My work.</p>
              {recovery.offer !== null && <RecoveryBanner at={recovery.offer.at} onKeep={recovery.keep} onDiscard={recovery.discard} />}
              <textarea
                id="quick-note-body"
                ref={field}
                // 16px keeps a phone from zooming in when the field takes focus.
                className={classes(inputClass, "min-h-40 sm:min-h-28")}
                value={body}
                onChange={(event) => {
                  setBody(event.target.value);
                  setSaved(false);
                }}
                onKeyDown={(event) => {
                  // Ctrl+Enter or Cmd+Enter saves, so a keyboard never needs the button.
                  if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                    event.preventDefault();
                    if (body.trim() !== "") write.mutate();
                  }
                }}
                maxLength={4000}
                enterKeyHint="done"
              />
              {problem !== null && <Problem message={problem} />}
              {saved && <p className="text-sm text-success">Saved. It waits in My work.</p>}
              <Button type="submit" disabled={write.isPending || body.trim() === ""}>
                Save the note
              </Button>
            </form>
          </div>
        </>
      )}
    </>
  );
}

/** The filed notes on one record. */
export function NotesOnRecord({
  recordType,
  recordId,
  showEmpty = false,
}: NoteLink & { readonly showEmpty?: boolean }) {
  const notes = useQuery({
    queryKey: ["notes-on", recordType, recordId],
    queryFn: () =>
      api.get<{ notes: NoteRow[]; note: string }>(`/api/notes/on/${recordType}/${recordId}`),
  });
  if ((notes.data?.notes.length ?? 0) === 0) {
    return showEmpty ? (
      <p className="text-sm text-muted">No quick notes yet.</p>
    ) : null;
  }
  return (
    <section aria-label="Quick notes" className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Quick notes</h3>
      <Note>{notes.data?.note}</Note>
      <ul className="flex flex-col gap-2">
        {(notes.data?.notes ?? []).map((row) => (
          <li key={row.id} className="rounded-arc border border-divider p-2 text-sm">
            <p className="whitespace-pre-wrap">{row.body}</p>
            <p className="mt-1 text-muted">
              {row.authorName}, {row.filedAt === null ? "" : dateOnly(row.filedAt)}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * A row of a list that opens the notes of one record.
 *
 * A list row is a poor place for a note thread, so the thread opens beneath
 * the row on request and closes again.
 */
export function NotesRow({
  recordType,
  recordId,
  colSpan,
  open,
}: NoteLink & { readonly colSpan: number; readonly open: boolean }) {
  if (!open) {
    return null;
  }
  return (
    <tr className="border-b border-divider bg-canvas">
      <td colSpan={colSpan} className="px-4 py-3">
        <NotesOnRecord recordType={recordType} recordId={recordId} showEmpty />
      </td>
    </tr>
  );
}
